import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Navigate } from 'react-router-dom';
import { useConfigureAuth } from '../hooks/UseConfigureAuth.hook';
import CircularProgress from '@mui/material/CircularProgress';
import { useSaveLocation } from '../hooks/UseSaveLocation.hook';
import { useEffect, useMemo, useState } from 'react';
import {
  EventType,
  InteractionType,
  IPublicClientApplication,
} from '@azure/msal-browser';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Snackbar,
  Stack,
  styled,
  Box,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useCopyToClipboard } from 'react-use';
import type { Theme } from '@dispatch/web/theme';

export const DarkBox = styled(Box)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette.dark.main,
}));

type MsalEventCallbackParams = {
  eventType: EventType;
  error: {
    errorMessage: string | string[];
  };
  interactionType: InteractionType;
  payload: {
    idTokenClaims: {
      [x: string]: string;
    };
  };
};

/**
 * Handles redirect request from AAD B2C after a successful login
 */
export function AuthRedirect() {
  useConfigureAuth();
  const { instance } = useMsal();
  const [error, setError] = useState<MsalEventCallbackParams | undefined>(
    undefined
  );
  const [_, copyToClipboard] = useCopyToClipboard();
  const [copyNotification, setCopyNotification] = useState(false);
  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setCopyNotification(false);
  };

  function handleCopy() {
    const message = `${error?.error?.errorMessage}`;
    copyToClipboard(message);
    setCopyNotification(true);
  }

  useEffect(() => {
    function handleLoginFailureEvent(
      event: MsalEventCallbackParams,
      _: IPublicClientApplication
    ) {
      if (event.eventType === EventType.LOGIN_FAILURE) {
        setError(event);
      }
    }

    const callbackRef =
      instance.addEventCallback((event: MsalEventCallbackParams) => {
        handleLoginFailureEvent(event, instance);
      }) || '';
    return () => instance.removeEventCallback(callbackRef);
  }, [instance]);

  const isAuthenticated = useIsAuthenticated();
  const [savedLocation] = useSaveLocation();

  const state: 'loading' | 'error' | 'authenticated' = useMemo(() => {
    if (isAuthenticated) {
      return 'authenticated';
    }

    if (error) {
      return 'error';
    }

    return 'loading';
  }, [error, isAuthenticated]);

  if (state === 'authenticated') {
    // this prevents users who are successfully authenticated when signing in from getting sent to an auth-redirect page
    if (savedLocation?.pathname === '/auth-redirect') {
      return <Navigate to={'/'} />;
    }

    return <Navigate to={savedLocation || '/'} />;
  }

  if (state === 'error') {
    return (
      <DarkBox
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        height="100vh"
      >
        <Card sx={{ maxWidth: '65%' }}>
          <CardContent>
            <Typography variant="h5">
              Sorry, but we're having trouble signing you in
            </Typography>

            <Typography
              variant="body1"
              sx={{ marginTop: '1em', marginBottom: '3em' }}
            >
              We track these errors automatically, but if the problem persists
              feel free to contact us. In the meantime, please try again.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1">Error Details</Typography>
              <IconButton onClick={handleCopy} aria-label="delete" size="small">
                <ContentCopyIcon
                  fontSize="inherit"
                  sx={{ marginLeft: 'auto' }}
                />
              </IconButton>
            </Stack>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption">
                  {error?.error.errorMessage}
                </Typography>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={copyNotification}
          onClose={handleClose}
          message="Error copied to clipboard"
        />
      </DarkBox>
    );
  }

  return (
    <DarkBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
      }}
      height="100vh"
    >
      <CircularProgress size="8em" thickness={1.5} color="inherit" />
    </DarkBox>
  );
}
